import {
  ArrowLeft,
  ArrowRight,
  Broom,
  CheckCircle,
  Info,
  LockKey,
  Play,
  X,
} from '@phosphor-icons/react'
import { useRef, useState } from 'react'
import type { UseBoqWorkspaceResult } from '../../hooks/useBoqWorkspace'
import type { ColumnMapping, MatchResult, SourceType } from '../../types'
import { ResultsView } from '../results/ResultsView'
import { AdvancedSettings } from './AdvancedSettings'
import { ConfirmationPanel } from './ConfirmationPanel'
import { FileDropZone } from './FileDropZone'
import { isMappingReady, MappingPanel, type MappingPreview } from './MappingPanel'
import { StepRail, type WorkflowStep } from './StepRail'

interface WorkspaceProps {
  initialStep: WorkflowStep
  workspace: UseBoqWorkspaceResult
}

interface ToastMessage {
  id: number
  title: string
  message: string
  tone: 'info' | 'error'
}

function previewFor(source: UseBoqWorkspaceResult['sources'][SourceType]): MappingPreview {
  if (!source.parsed) return { headers: [], rows: [] }
  return {
    headers: source.parsed.headerCells.map((cell) => cell.displayText),
    rows: source.parsed.previewRows.map((row) => row.cells.map((cell) => cell.displayText)),
    sourceRowNumbers: source.parsed.previewRows.map((row) => row.sourceRowNumber),
  }
}

function sourceWarning(source: UseBoqWorkspaceResult['sources'][SourceType]): string | null {
  const warnings = [
    ...(source.metadata?.warnings ?? []),
    ...(source.parsed?.warnings ?? []),
    ...(source.mapped?.warnings ?? []),
  ]
  return warnings[0] ?? null
}

function sourceFileSummary(source: UseBoqWorkspaceResult['sources'][SourceType]) {
  return source.metadata
    ? {
        fileName: source.metadata.fileName,
        fileSize: source.metadata.fileSize,
        sheetCount: source.metadata.sheetCount,
      }
    : null
}

function resultPendingForReview(result: MatchResult): boolean {
  return result.status === 'pending' && result.confirmationState === 'pending'
}

function StepOne({
  onContinue,
  workspace,
}: {
  onContinue: () => void
  workspace: UseBoqWorkspaceResult
}) {
  const ready = Boolean(workspace.sources.baseline.metadata && workspace.sources.target.metadata)
  return (
    <div className="workspace-page">
      <div className="workspace-page__heading">
        <div>
          <h1>导入两版清单</h1>
          <p>文件在浏览器内存中解析。基准版和对比版可以使用不同的列顺序与标题。</p>
        </div>
      </div>
      <div className="file-pair">
        {(['baseline', 'target'] as const).map((sourceType) => {
          const source = workspace.sources[sourceType]
          return (
            <FileDropZone
              key={sourceType}
              sourceType={sourceType}
              busy={source.busy}
              error={source.error}
              file={sourceFileSummary(source)}
              onFile={(file) => void workspace.loadFile(sourceType, file)}
              onClear={() => void workspace.clearSource(sourceType)}
            />
          )
        })}
      </div>
      <div className="workflow-footer">
        <span className="workflow-footer__note">
          <LockKey aria-hidden="true" weight="fill" />
          不会上传文件，也不会把文件名或工作簿内容写入本地存储。
        </span>
        <div className="workflow-footer__actions">
          <button
            className="button button--primary"
            type="button"
            disabled={!ready}
            onClick={onContinue}
          >
            选择工作表与字段
            <ArrowRight aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  )
}

function StepTwo({
  onBack,
  onCompared,
  notify,
  workspace,
}: {
  onBack: () => void
  onCompared: (results: readonly MatchResult[]) => void
  notify: (title: string, message: string, tone?: ToastMessage['tone']) => void
  workspace: UseBoqWorkspaceResult
}) {
  const baseline = workspace.sources.baseline
  const target = workspace.sources.target
  const ready = isMappingReady(baseline.mapping) && isMappingReady(target.mapping)

  const runComparison = async (): Promise<void> => {
    try {
      const results = await workspace.compare()
      onCompared(results)
      notify('对比完成', `已形成 ${results.length.toLocaleString('zh-CN')} 条差异结果。`)
    } catch (error) {
      notify(
        '无法开始对比',
        error instanceof Error ? error.message : '请检查字段映射后重试。',
        'error',
      )
    }
  }

  if (!baseline.metadata || !target.metadata) {
    return (
      <div className="empty-state">
        <div>
          <Info aria-hidden="true" weight="duotone" />
          <h2>请先导入两份清单</h2>
          <p>基准版与对比版都准备好后，才能独立确认工作表和字段映射。</p>
          <button className="button button--primary" type="button" onClick={onBack}>
            返回导入
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="workspace-page">
      <div className="workspace-page__heading">
        <div>
          <h1>确认工作表与字段映射</h1>
          <p>系统扫描前 30 个非空行自动识别。请分别核对两版标题行与字段。</p>
        </div>
      </div>
      <div className="mapping-pair">
        {(['baseline', 'target'] as const).map((sourceType) => {
          const source = workspace.sources[sourceType]
          return (
            <MappingPanel
              key={sourceType}
              sourceType={sourceType}
              sheets={(source.metadata?.sheets ?? []).map((sheet) => ({
                name: sheet.name,
                rowCount: sheet.nonEmptyRowCount,
              }))}
              selectedSheet={source.selectedSheet}
              mapping={source.mapping}
              preview={previewFor(source)}
              warning={sourceWarning(source)}
              onSheetChange={(sheet) => void workspace.selectSheet(sourceType, sheet)}
              onHeaderRowChange={(row) => void workspace.setHeaderRow(sourceType, row)}
              onMappingChange={(field, column) =>
                workspace.setMappingField(sourceType, field, column)
              }
            />
          )
        })}
      </div>
      <AdvancedSettings settings={workspace.settings} onChange={workspace.setSettings} />
      {!ready ? (
        <p className="inline-message inline-message--warning" role="status">
          项目名称、单位、工程量、综合单价以及编码或项目特征必须映射后才能比较。
        </p>
      ) : null}
      <div className="workflow-footer">
        <button className="button button--secondary" type="button" onClick={onBack}>
          <ArrowLeft aria-hidden="true" />
          返回导入
        </button>
        <div className="workflow-footer__actions">
          <button
            className="button button--primary"
            data-testid="run-comparison"
            type="button"
            disabled={!ready || workspace.comparing || baseline.busy || target.busy}
            onClick={() => void runComparison()}
          >
            <Play aria-hidden="true" weight="fill" />
            {workspace.comparing ? '正在匹配' : '开始比较'}
          </button>
        </div>
      </div>
    </div>
  )
}

function StepThree({
  onBack,
  onResults,
  workspace,
}: {
  onBack: () => void
  onResults: () => void
  workspace: UseBoqWorkspaceResult
}) {
  const pending = workspace.results.filter(resultPendingForReview)
  const [index, setIndex] = useState(0)
  const safeIndex = Math.min(index, Math.max(0, pending.length - 1))
  const active = pending[safeIndex] ?? null
  const reviewable = workspace.results.filter(
    (result) => result.manualDecision !== null || resultPendingForReview(result),
  )
  const reviewedCount = reviewable.filter((result) => result.manualDecision !== null).length
  const progressPercent =
    reviewable.length === 0 ? 100 : Math.min(100, (reviewedCount / reviewable.length) * 100)
  const lastDecided = workspace.lastDecidedId
    ? (workspace.results.find((result) => result.id === workspace.lastDecidedId) ?? null)
    : null

  return (
    <div className="workspace-page">
      <div className="workspace-page__heading">
        <div>
          <h1>处理待确认匹配</h1>
          <p>候选得分接近或低于高可信阈值时，由你确认身份关系。</p>
        </div>
      </div>
      <div className="stage-progress" role="group" aria-label="待确认处理进度">
        <div className="stage-progress__heading">
          <strong>剩余 {pending.length} 条</strong>
          <span>{pending.length === 0 ? '全部处理完成' : `正在处理第 ${safeIndex + 1} 条`}</span>
        </div>
        <div
          className="stage-progress__track"
          role="progressbar"
          aria-label="待确认处理完成比例"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progressPercent)}
        >
          <span
            style={{
              width: `${progressPercent}%`,
            }}
          />
        </div>
      </div>
      <ConfirmationPanel
        active={active}
        currentIndex={safeIndex}
        total={pending.length}
        lastDecided={lastDecided}
        onPrevious={() => setIndex((current) => Math.max(0, current - 1))}
        onNext={() => setIndex((current) => Math.min(pending.length - 1, current + 1))}
        onDecision={workspace.applyDecision}
        onUndo={workspace.undoDecision}
      />
      <div className="workflow-footer">
        <button className="button button--secondary" type="button" onClick={onBack}>
          <ArrowLeft aria-hidden="true" />
          返回字段映射
        </button>
        <div className="workflow-footer__actions">
          <button className="button button--primary" type="button" onClick={onResults}>
            查看差异结果
            <ArrowRight aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  )
}

function StepFour({
  notify,
  onReviewPending,
  workspace,
}: {
  notify: (title: string, message: string, tone?: ToastMessage['tone']) => void
  onReviewPending: () => void
  workspace: UseBoqWorkspaceResult
}) {
  const exportReport = async (): Promise<void> => {
    try {
      const fileName = await workspace.exportReport()
      notify('报告已生成', `${fileName} 已交给浏览器下载。`)
    } catch (error) {
      notify('导出失败', error instanceof Error ? error.message : '无法生成报告，请重试。', 'error')
    }
  }

  if (!workspace.summary) {
    return (
      <div className="empty-state">
        <div>
          <Info aria-hidden="true" weight="duotone" />
          <h2>还没有对比结果</h2>
          <p>完成字段映射并运行比较后，差异与金额影响会显示在这里。</p>
        </div>
      </div>
    )
  }

  return (
    <ResultsView
      results={workspace.results}
      summary={workspace.summary}
      exporting={workspace.exporting}
      onExport={() => void exportReport()}
      onReviewPending={onReviewPending}
    />
  )
}

export function Workspace({ initialStep, workspace }: WorkspaceProps) {
  const [current, setCurrent] = useState<WorkflowStep>(initialStep)
  const [maxReached, setMaxReached] = useState<WorkflowStep>(initialStep)
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const toastSequence = useRef(0)
  const visibleMaxReached = workspace.results.length === 0 && maxReached > 2 ? 2 : maxReached
  const visibleCurrent = workspace.results.length === 0 && current > 2 ? 2 : current

  const notify = (title: string, message: string, tone: ToastMessage['tone'] = 'info'): void => {
    toastSequence.current += 1
    const id = toastSequence.current
    setToasts((currentToasts) => [...currentToasts, { id, title, message, tone }])
    window.setTimeout(() => {
      setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id))
    }, 5000)
  }

  const goTo = (step: WorkflowStep): void => {
    setCurrent(step)
    setMaxReached((reached) => Math.max(reached, step) as WorkflowStep)
  }

  const restart = async (): Promise<void> => {
    await workspace.clearAll()
    setCurrent(1)
    setMaxReached(1)
    notify('数据已清空', '工作簿内容和对比结果已从浏览器内存释放。')
  }

  const compared = (results: readonly MatchResult[]): void => {
    const hasPending = results.some(resultPendingForReview)
    goTo(hasPending ? 3 : 4)
    setMaxReached(4)
  }

  const mainClass = visibleCurrent === 4 ? 'workspace-main workspace-main--wide' : 'workspace-main'
  return (
    <div className="workspace-layout">
      <StepRail current={visibleCurrent} maxReached={visibleMaxReached} onStepChange={goTo} />
      <main className={mainClass}>
        <div
          className={visibleCurrent === 4 ? 'workspace-page workspace-page--results' : undefined}
        >
          <div className="workspace-utility-row">
            <span>
              {workspace.comparedAt
                ? `最近比较：${workspace.comparedAt.toLocaleString('zh-CN')}`
                : '文件处理范围：当前浏览器内存'}
            </span>
            <button className="button button--ghost" type="button" onClick={() => void restart()}>
              <Broom aria-hidden="true" />
              重新开始并清空数据
            </button>
          </div>
          {visibleCurrent === 1 ? (
            <StepOne workspace={workspace} onContinue={() => goTo(2)} />
          ) : null}
          {visibleCurrent === 2 ? (
            <StepTwo
              workspace={workspace}
              onBack={() => goTo(1)}
              onCompared={compared}
              notify={notify}
            />
          ) : null}
          {visibleCurrent === 3 ? (
            <StepThree workspace={workspace} onBack={() => goTo(2)} onResults={() => goTo(4)} />
          ) : null}
          {visibleCurrent === 4 ? (
            <StepFour workspace={workspace} notify={notify} onReviewPending={() => goTo(3)} />
          ) : null}
        </div>
      </main>

      <div className="toast-region" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div className={`toast toast--${toast.tone}`} key={toast.id} role="status">
            {toast.tone === 'error' ? (
              <Info aria-hidden="true" weight="fill" />
            ) : (
              <CheckCircle aria-hidden="true" weight="fill" />
            )}
            <div>
              <strong>{toast.title}</strong>
              <span>{toast.message}</span>
            </div>
            <button
              type="button"
              onClick={() =>
                setToasts((currentToasts) => currentToasts.filter((item) => item.id !== toast.id))
              }
              aria-label="关闭提示"
            >
              <X aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

export function hasRequiredMappings(mapping: ColumnMapping): boolean {
  return isMappingReady(mapping)
}
